import { DEVICE_TYPE_LABELS, DEVICE_STATUS_LABELS } from '@company/shared'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, getPaginated, post, put, del } from '@/lib/api'

import type { DeviceType, DeviceStatus } from '@company/shared'


// ==================== Types ====================

export type { DeviceType, DeviceStatus }

export interface Device {
  id: string
  name: string
  type: DeviceType
  brand: string | null
  model: string | null
  serialNumber: string
  purchaseDate: string | null
  purchasePrice: number | null
  status: DeviceStatus
  currentAssigneeId: string | null
  notes: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface DeviceWithAssignee extends Device {
  assignee?: {
    id: string
    name: string
    employeeNo: string
  } | null
}

export interface DeviceAssignment {
  id: string
  deviceId: string
  employeeId: string
  assignedAt: string
  assignedBy: string
  assignNotes: string | null
  returnedAt: string | null
  returnedBy: string | null
  returnNotes: string | null
  createdAt: string
}

export interface StatusLog {
  id: string
  fromStatus: DeviceStatus | null
  toStatus: DeviceStatus
  changedBy: string
  changedByName: string
  changedAt: string
  notes: string | null
}

export interface DeviceWithHistory extends Device {
  assignee?: {
    id: string
    name: string
    employeeNo: string
  } | null
  assignments: DeviceAssignment[]
  statusLogs: StatusLog[]
}

export interface DeviceWithAssignment extends Device {
  assignment?: {
    assignedAt: string
    assignNotes?: string | null
  }
}

export interface CreateDeviceInput {
  name: string
  type: DeviceType
  brand?: string
  model?: string
  serialNumber: string
  purchaseDate?: string
  purchasePrice?: number
  notes?: string
}

export interface UpdateDeviceInput {
  name?: string
  type?: DeviceType
  brand?: string
  model?: string
  serialNumber?: string
  purchaseDate?: string
  purchasePrice?: number
  notes?: string
}

export interface AssignDeviceInput {
  employeeId: string
  notes?: string
}

export interface ReturnDeviceInput {
  notes?: string
}

export interface StatusChangeInput {
  notes?: string
}

export interface DeviceFilters {
  type?: DeviceType
  status?: DeviceStatus
  assigneeId?: string
  search?: string
  page?: number
  pageSize?: number
}

export interface DeviceExportItem {
  id: string
  name: string
  type: string
  typeName: string
  brand: string | null
  model: string | null
  serialNumber: string
  purchaseDate: string | null
  purchasePrice: number | null
  status: string
  statusName: string
  assigneeName: string | null
  assigneeNo: string | null
  assignedAt: string | null
  notes: string | null
  createdAt: string
}

// ==================== Query Keys ====================

export const deviceKeys = {
  all: ['devices'] as const,
  lists: () => [...deviceKeys.all, 'list'] as const,
  list: (filters: DeviceFilters) => [...deviceKeys.lists(), filters] as const,
  details: () => [...deviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...deviceKeys.details(), id] as const,
  history: (id: string) => [...deviceKeys.all, 'history', id] as const,
  export: (filters: DeviceFilters) => [...deviceKeys.all, 'export', filters] as const,
  myDevices: () => [...deviceKeys.all, 'my'] as const,
  employeeDevices: (employeeId: string) => [...deviceKeys.all, 'employee', employeeId] as const,
}

// ==================== Hooks ====================

/**
 * 获取设备列表（分页）
 */
export function useDevices(filters: DeviceFilters = {}) {
  return useQuery({
    queryKey: deviceKeys.list(filters),
    queryFn: () => getPaginated<DeviceWithAssignee>('/admin/devices', filters as Record<string, unknown>),
  })
}

/**
 * 获取设备详情（含历史）
 */
export function useDevice(id: string) {
  return useQuery({
    queryKey: deviceKeys.detail(id),
    queryFn: () => get<DeviceWithHistory>(`/admin/devices/${id}`),
    enabled: !!id,
  })
}

/**
 * 获取设备分配历史
 */
export function useDeviceHistory(id: string) {
  return useQuery({
    queryKey: deviceKeys.history(id),
    queryFn: () => get<DeviceAssignment[]>(`/admin/devices/${id}/history`),
    enabled: !!id,
  })
}

/**
 * 导出设备数据
 */
export function useDeviceExport(filters: DeviceFilters = {}) {
  return useQuery({
    queryKey: deviceKeys.export(filters),
    queryFn: () => get<DeviceExportItem[]>('/admin/devices/export', filters as Record<string, unknown>),
    enabled: false, // 手动触发
  })
}

/**
 * 获取我的设备
 */
export function useMyDevices() {
  return useQuery({
    queryKey: deviceKeys.myDevices(),
    queryFn: () => get<DeviceWithAssignment[]>('/users/me/devices'),
  })
}

/**
 * 获取员工设备
 */
export function useEmployeeDevices(employeeId: string) {
  return useQuery({
    queryKey: deviceKeys.employeeDevices(employeeId),
    queryFn: () => get<DeviceWithAssignment[]>(`/hr/employees/${employeeId}/devices`),
    enabled: !!employeeId,
  })
}

/**
 * 创建设备
 */
export function useCreateDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDeviceInput) => post<Device>('/admin/devices', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
    },
  })
}

/**
 * 更新设备
 */
export function useUpdateDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeviceInput }) =>
      put<Device>(`/admin/devices/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
    },
  })
}

/**
 * 删除设备
 */
export function useDeleteDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/admin/devices/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
    },
  })
}

/**
 * 分配设备
 */
export function useAssignDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignDeviceInput }) =>
      post<DeviceAssignment>(`/admin/devices/${id}/assign`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.myDevices() })
    },
  })
}

/**
 * 归还设备
 */
export function useReturnDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReturnDeviceInput }) =>
      post<DeviceAssignment>(`/admin/devices/${id}/return`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.myDevices() })
    },
  })
}

/**
 * 标记设备维修中
 */
export function useMarkDeviceMaintenance() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StatusChangeInput }) =>
      post<Device>(`/admin/devices/${id}/maintenance`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
    },
  })
}

/**
 * 标记设备已报废
 */
export function useMarkDeviceRetired() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StatusChangeInput }) =>
      post<Device>(`/admin/devices/${id}/retire`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
    },
  })
}

/**
 * 恢复设备为可用
 */
export function useRestoreDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StatusChangeInput }) =>
      post<Device>(`/admin/devices/${id}/restore`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: deviceKeys.lists() })
    },
  })
}

// ==================== Helper Functions ====================

/**
 * 获取设备类型中文名称
 */
export function getDeviceTypeName(type: DeviceType): string {
  return DEVICE_TYPE_LABELS[type] ?? type
}

/**
 * 获取设备状态中文名称
 */
export function getDeviceStatusName(status: DeviceStatus): string {
  return DEVICE_STATUS_LABELS[status] ?? status
}

/**
 * 获取设备状态颜色
 */
export function getDeviceStatusColor(status: DeviceStatus): string {
  const statusColors: Record<DeviceStatus, string> = {
    available: 'bg-green-100 text-green-800',
    assigned: 'bg-blue-100 text-blue-800',
    maintenance: 'bg-yellow-100 text-yellow-800',
    retired: 'bg-gray-100 text-gray-800',
  }
  return statusColors[status] ?? 'bg-gray-100 text-gray-800'
}
